import React, { useEffect, useMemo, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { ReviewsMobileViewCell } from 'Deer/components/Reviews/ReviewsMobileViewCell';
import { getDeerReviewResultRoute } from 'Deer/constants/nonDeerRoutes';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { format, parseISO } from 'date-fns';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import 'styles/agGrid.css';
import { agGridDateComparator } from 'utils/helpers/dateHelpers';
import { checkIntentLandingPagesFeatureEnabled, checkNewNavEnabled } from 'utils/helpers/featureFlags';

import NoDataImg from 'assets/assetsV1/illustrations/noReviews.svg';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { downloadOrOpenFile, getPersistenceKey, getReviewResultRoute } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { sendReviewSummaryPageViewEvent } from 'constants/mixpanelHelper';
import {
  sendClickedOnAcknowledgeEvent,
  sendClickedPastReviewsViewDetailsEvent,
} from 'constants/mixpanelHelpers/reviewsEvents';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { DateComp } from 'uiToolkitV1/DateComp';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { showErrorMessage, showLoader } from '../components/common';
import { generatePastReviewsOptions } from './utils';

const TABLE_COLUMNS_DISPLAYNAME = {
  REVIEW_CYCLE: 'Review Cycle',
  INSTANCE: 'Instance',
  PUBLISHED_ON: 'Published On',
  SCORE: 'Score',
  EXPANDED_MENU: 'Expanded Menu',
};

// need to do this because agGrid `valueFormatter` doesnt have any impact on exports
function getFormattedValue({ value, api, column }) {
  const displayName = api.getDisplayNameForColumn(column);
  switch (displayName) {
    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_CYCLE: {
      return value;
    }

    case TABLE_COLUMNS_DISPLAYNAME.INSTANCE: {
      return value;
    }

    case TABLE_COLUMNS_DISPLAYNAME.PUBLISHED_ON: {
      if (!value) return 'NA';
      const date = parseISO(new Date(value).toISOString());
      return format(date, 'yyyy-MM-dd') ?? 'NA';
    }

    case TABLE_COLUMNS_DISPLAYNAME.SCORE: {
      return value;
    }

    default:
      return new Error('Invalid column name');
  }
}

function getCellContent(displayName, cellId, rowData, value) {
  if (isMobileView()) {
    switch (displayName) {
      case TABLE_COLUMNS_DISPLAYNAME.REVIEW_CYCLE: {
        const publishedOn = rowData[3]?.value;
        const score = rowData[4]?.value;

        return (
          <ReviewsMobileViewCell
            name={value}
            subTitle={publishedOn ? `Published on: ${format(publishedOn, 'MMM dd, yyyy')}` : ''}
            description={{
              title: 'Score',
              text: score || 'NA',
            }}
          />
        );
      }
      default:
        return null;
    }
  }

  switch (displayName) {
    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_CYCLE: {
      return (
        <Box className="info">
          <TextBodyMedium>{value}</TextBodyMedium>
        </Box>
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME.INSTANCE: {
      return (
        <Box className="info">
          <TextBodyMedium>{value}</TextBodyMedium>
        </Box>
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME.PUBLISHED_ON: {
      return (
        <Box className="info">
          {value ? (
            <DateComp date={value} isRelativeDateFormat={false} enableRelativeFormatDistanceColor={false} />
          ) : (
            <TextBodyMedium>NA</TextBodyMedium>
          )}
        </Box>
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME.SCORE: {
      return (
        <Box className="info">
          <TextBodyMedium>{value}</TextBodyMedium>
        </Box>
      );
    }

    default:
      return new Error('Invalid column name');
  }
}

function CellRenderer(params) {
  return getCellContent(params.colDef.displayName, params.colDef.id, params.data, params.value);
}

function getColProps(col) {
  const defaultColProps = {
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    headerComponent: CustomHeaderCell,
  };

  switch (col.displayName) {
    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_CYCLE: {
      return {
        ...defaultColProps,
        sortable: true,
        valueGetter: (params) => params?.data[params.colDef.id]?.value,
        cellRenderer: CellRenderer,
        filter: 'agTextColumnFilter',
        width: '25%',
      };
    }
    case TABLE_COLUMNS_DISPLAYNAME.INSTANCE: {
      return {
        ...defaultColProps,
        sortable: true,
        filter: 'agTextColumnFilter',
        valueGetter: (params) => params?.data[params.colDef.id].value,
        width: '25%',
        cellRenderer: CellRenderer,
      };
    }

    case TABLE_COLUMNS_DISPLAYNAME.PUBLISHED_ON: {
      return {
        ...defaultColProps,
        sortable: true,
        valueGetter: (params) => params?.data[params.colDef.id]?.value,
        cellRenderer: CellRenderer,
        width: '25%',
        filter: 'agDateColumnFilter',
        filterValueGetter: ({ data }) => {
          if (!data[3]?.value) return null;
          return data[3]?.value;
        },
        filterParams: {
          comparator: agGridDateComparator,
        },
      };
    }

    case TABLE_COLUMNS_DISPLAYNAME.SCORE: {
      return {
        ...defaultColProps,
        sortable: true,
        valueGetter: (params) => params?.data[params.colDef.id]?.value,
        cellRenderer: CellRenderer,
        filter: 'agTextColumnFilter',
      };
    }

    default:
      return new Error('Invalid column name');
  }
}

const ReviewsPastAGGrid = ({ selectedTableRef, columns = [], rows = [], selectedTableId, loading, errorMessage }) => {
  const [rowData, setRowData] = useState([]);

  const [columnDefs, setColumnDefs] = useState([]);
  const history = useHistory();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  const goToFormReviewRoute = (reviewId, employeeId) => {
    const routeToGo = checkNewNavEnabled()
      ? `${getDeerReviewResultRoute(reviewId, employeeId)}?source=${
          checkIntentLandingPagesFeatureEnabled() ? 'Me' : selectedTableId
        }&subSource=${selectedTableId}&defaultSelectedProviderId=${loggedInEmployee.id}`
      : `${getReviewResultRoute(reviewId, employeeId)}?source=${selectedTableId}&defaultSelectedProviderId=${
          loggedInEmployee.id
        }`;
    history.push(routeToGo);
  };

  const handleViewDetailsClick = ({ reviewItem }) => {
    const { review } = reviewItem;

    if (review?.summaryFilePath) {
      downloadOrOpenFile(review.summaryFilePath);
    } else {
      goToFormReviewRoute(review.feedbackForm.id, review.receiverEmployeeId);
    }
    sendReviewSummaryPageViewEvent({ eventProperties: { source: 'My past reviews' } });

    sendClickedPastReviewsViewDetailsEvent({
      eventProperties: {
        reviewCycleId: review.id,
        empId: loggedInEmployee.id,
        source: 'MyReviewsPast',
      },
    });
  };

  useEffect(() => {
    if (columns.length === 0) return;

    if (isMobileView()) {
      // Only show the first column in mobile view
      const mobileColumnDefs = columns.slice(0, 1).map((col) => ({
        colId: col.id,
        field: col.id,
        id: col.id,
        displayName: col.displayName,
        minWidth: 255,
        flex: 1,
        headerComponent: CustomHeaderCell,
        headerComponentParams: {
          suppressHeaderMenuButton: true,
          enableSorting: false,
        },
        headerValueGetter: () => col.displayName,
        ...getColProps(col),
        pinned: undefined,
        lockPosition: undefined,
        suppressMovable: true,
        suppressSizeToFit: false,
      }));
      setColumnDefs(mobileColumnDefs);
    } else {
      const newColumnDefs = columns.map((col) => {
        const defaultColsProps = {
          colId: col.id,
          field: col.id,
          id: col.id,
          displayName: col.displayName,
          minWidth: 220,
          hide: false,
          flex: 1,
          headerComponent: CustomHeaderCell,
          headerComponentParams: {
            enableMenu: true,
            enableSorting: true,
          },
          headerValueGetter: () => col.displayName,
          ...getColProps(col),
        };
        if (col.displayName === 'Review Cycle' || col.displayName === 'Published On') {
          return {
            ...defaultColsProps,
            lockVisible: true,
          };
        }
        return defaultColsProps;
      });

      setColumnDefs(newColumnDefs);
    }
  }, [columns]);

  useEffect(() => {
    setRowData(rows);
  }, [rows]);

  /* Grid State persistence Key */
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.REVIEW;
    const viewId = 'MyReviews';
    const tabId = 'Explore';
    const uniqueId = 'MyPastReviewsGrid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const handleAcknowledgeClick = ({ reviewItem }) => {
    const { review } = reviewItem;
    sendClickedOnAcknowledgeEvent({
      eventProperties: {
        reviewCycleId: review.feedbackForm.id,
        empId: review.receiverEmployeeId,
        source: 'MyReviewsPastReviews',
      },
    });
    if (review?.summaryFilePath) {
      downloadOrOpenFile(review.summaryFilePath);
    } else {
      const routeToGo = checkNewNavEnabled()
        ? `${getDeerReviewResultRoute(review.feedbackForm.id, review.receiverEmployeeId)}?autoNavigate=true&source=${
            checkIntentLandingPagesFeatureEnabled() ? 'Me' : selectedTableId
          }&subSource=${selectedTableId}`
        : `${getReviewResultRoute(review.feedbackForm.id, review.receiverEmployeeId)}?autoNavigate=true`;
      history.push(routeToGo);
    }
  };

  const onCellClickHandler = ({ data, column }) => {
    const {
      1: { reviewItem },
    } = data;
    const { isFormAcknowledged } = reviewItem;

    const isPendingForAcknowledgement = !isFormAcknowledged;

    if (column.colId === 'kebab-menu') {
      //  To prevent event bubbling.
    } else {
      let primaryActionRowClickHandler = handleViewDetailsClick;
      if (isPendingForAcknowledgement) {
        primaryActionRowClickHandler = handleAcknowledgeClick;
      }
      primaryActionRowClickHandler({ reviewItem });
    }
  };

  if (loading) {
    return showLoader();
  }
  if (rows === null) {
    return showErrorMessage({ errorText: errorMessage });
  }
  if (rows.length === 0) {
    return (
      <div style={{ marginTop: '60px' }}>
        <EmptyScreen heading="Nothing to see here, yet!" illustrationSrc={NoDataImg} />
      </div>
    );
  }

  return (
    <Box className="review">
      <AgGridTable
        key={gridStatePersistenceKey} // we want to re-initialize on key change
        gridStatePersistenceKey={gridStatePersistenceKey}
        ref={selectedTableRef}
        rowData={rowData}
        columnDefs={columnDefs}
        domLayout="autoHeight"
        pagination
        suppressPaginationPanel={rowData?.length < 6 || rowData?.length === undefined || rowData?.length === 0}
        paginationPageSize={isMobileView() ? 5 : 10}
        sideBar={!isMobileView() && sideBar}
        headerHeight={36}
        autoHeight={false}
        rowHeight={isMobileView() ? 100 : 56}
        suppressCellFocus
        onCellClicked={onCellClickHandler}
        defaultCsvExportParams={{
          processCellCallback: getFormattedValue,
          fileName: 'My_past_reviews.csv',
          columnKeys: columns.map((c) => c.id),
        }}
        kebabMenuIsRowSpecific
        kebabMenuOptionsGenerator={(data) => {
          const options = generatePastReviewsOptions({
            data,
            history,
            loggedInEmployee,
            selectedTableId,
            isNewNavEnabled: checkNewNavEnabled(),
          });
          return options;
        }}
        containerAutoHeight
        additionalMobileProps={isMobileView() ? { pinned: undefined, lockPosition: undefined } : undefined}
        isMobileView={isMobileView()}
        paginationPageSizeSelector={isMobileView() ? false : [10, 20, 50]}
      />
    </Box>
  );
};

export default ReviewsPastAGGrid;
